/**
 * WVB-01 — Event Graph Tab (container)
 * Two synchronized views: Explorer (hierarchical) + Visual (node-link)
 * Both use the same tenant-authorized GET /api/wvb/events/{eid}/graph endpoint.
 */
import { useState, useEffect, useCallback } from "react";
import { Network, List, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { graphApi } from "@/services/api";
import EventGraphExplorer from "./EventGraphExplorer";
import EventGraphVisual from "./EventGraphVisual";
import { NODE_CONFIG } from "./EventGraphUtils";

export default function EventGraphTab({ eid }) {
  const [view, setView]           = useState("explorer");
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const loadGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await graphApi.getGraph(eid);
      setGraphData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load event graph");
    } finally {
      setLoading(false);
    }
  }, [eid]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        <span className="ml-3 text-slate-500">Building event graph…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <AlertTriangle className="w-10 h-10 text-amber-400" />
        <p className="text-slate-600">{error}</p>
        <Button variant="outline" size="sm" onClick={loadGraph}>
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  if (!graphData) return null;

  const meta = graphData.meta;

  return (
    <div className="space-y-4" data-testid="event-graph-tab">

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setView("explorer")}
              data-testid="graph-view-explorer"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                view === "explorer" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <List className="w-4 h-4" /> Explorer
            </button>
            <button
              onClick={() => setView("graph")}
              data-testid="graph-view-visual"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                view === "graph" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Network className="w-4 h-4" /> Graph
            </button>
          </div>
          <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
            {meta.node_count} nodes
          </span>
          <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
            {meta.edge_count} edges
          </span>
          {meta.warning_count > 0 && (
            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {meta.warning_count} warnings
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={loadGraph} data-testid="graph-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* ── Warnings panel ── */}
      {graphData.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Graph Warnings</span>
          </div>
          <ul className="space-y-1">
            {graphData.warnings.map((w, i) => (
              <li key={i} className="text-xs text-amber-700">
                <span className="font-medium">{w.node_type}</span> — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Views ── */}
      {view === "explorer" && <EventGraphExplorer graphData={graphData} eid={eid} />}
      {view === "graph"    && <EventGraphVisual    graphData={graphData} eid={eid} />}

      {/* ── Legend ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Entity Legend</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(NODE_CONFIG).map(([type, cfg]) => (
            <span
              key={type}
              className="text-xs px-2 py-1 rounded-full font-medium"
              style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
            >
              {cfg.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
